import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import DateTimePicker from '../../components/ui/DateTimePicker';
import { scheduleService } from '../../services/scheduleService';
import { format, addDays } from 'date-fns';

const ScheduleService = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    scheduledDate: '',
    scheduledTime: '',
    scheduledDateTime: null,
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const serviceOptions = [
    { value: 'drive-thru-cleanup', label: 'Drive Thru Clean Up' },
    { value: 'electrical-services', label: 'Electrical Services' },
    { value: 'equipment-repair', label: 'Equipment Repair' },
    { value: 'fat-traps', label: 'Fat Traps' },
    { value: 'painting', label: 'Painting' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'industrial-refrigerators', label: 'Industrial Refrigerators' },
    { value: 'road-markings-signage', label: 'Road Markings & Signage Boards' }
  ];

  const timeOptions = [
    { value: 'morning', label: 'Morning (8AM - 12PM)' },
    { value: 'afternoon', label: 'Afternoon (12PM - 5PM)' },
    { value: 'evening', label: 'Evening (5PM - 8PM)' }
  ];

  // Generate next 30 days for date selection
  const generateDateOptions = () => {
    const dates = [];
    for (let i = 1; i <= 30; i++) {
      const date = addDays(new Date(), i);
      dates?.push({
        value: format(date, 'yyyy-MM-dd'),
        label: format(date, 'EEEE, MMMM d, yyyy')
      });
    }
    return dates;
  };

  const dateOptions = generateDateOptions();

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.serviceType) {
      newErrors.serviceType = 'Please select a service type';
    }

    if (!formData?.scheduledDateTime) {
      newErrors.scheduledDateTime = 'Please select a date and time';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError('');

    const newErrors = validateForm();
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        scheduledDate: formData?.scheduledDateTime ? format(new Date(formData.scheduledDateTime), 'yyyy-MM-dd') : '',
        scheduledTime: formData?.scheduledDateTime ? format(new Date(formData.scheduledDateTime), 'HH:mm') : '',
      };
      const { data, error } = await scheduleService?.create(submitData);

      if (error) {
        throw error;
      }

      setSubmitSuccess(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        serviceType: '',
        scheduledDate: '',
        scheduledTime: '',
        scheduledDateTime: null,
        message: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      setSubmitError(error?.message || 'Failed to schedule service. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Icon name="Calendar" size={48} className="mx-auto mb-4" />
              <h1 className="font-headline font-bold text-3xl md:text-4xl lg:text-5xl mb-4">
                Schedule a Service
              </h1>
              <p className="font-body text-base md:text-lg opacity-90">
                Choose your preferred date and time for our expert technicians to visit
              </p>
            </div>
          </div>
        </section>

        {/* Booking Form Section */}
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              {submitSuccess ? (
                <div className="bg-success text-success-foreground rounded-lg p-6 md:p-8 text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-success-foreground text-success rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="CheckCircle" size={32} className="md:w-10 md:h-10" />
                  </div>
                  <h3 className="font-headline font-bold text-xl md:text-2xl mb-2">
                    Service Scheduled Successfully!
                  </h3>
                  <p className="font-body text-sm md:text-base mb-4">
                    We've received your appointment request. Our team will contact you shortly to confirm the details.
                  </p>
                  <p className="font-body text-sm opacity-90 mb-6">
                    For immediate assistance, call <span className="font-accent font-semibold">082 793 5785</span>
                  </p>
                  <Button onClick={() => setSubmitSuccess(false)} variant="outline">
                    Schedule Another Service
                  </Button>
                </div>
              ) : (
                <div className="bg-card rounded-lg p-6 md:p-8 shadow-sm border border-border">
                  <div className="mb-6">
                    <h2 className="font-headline font-bold text-xl md:text-2xl lg:text-3xl mb-2">
                      Book Your Appointment
                    </h2>
                    <p className="font-body text-sm md:text-base text-muted-foreground">
                      Select your preferred date, time, and service type
                    </p>
                  </div>

                  {submitError && (
                    <div className="bg-error/10 border border-error text-error rounded-lg p-3 mb-4 text-sm">
                      {submitError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                    {/* Contact Information */}
                    <div className="space-y-4">
                      <h3 className="font-headline font-semibold text-lg text-foreground">
                        Contact Information
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        <Input
                          label="Full Name"
                          type="text"
                          name="name"
                          placeholder="Enter your name"
                          value={formData?.name}
                          onChange={handleChange}
                          error={errors?.name}
                          required
                        />

                        <Input
                          label="Phone Number"
                          type="tel"
                          name="phone"
                          placeholder="082 123 4567"
                          value={formData?.phone}
                          onChange={handleChange}
                          error={errors?.phone}
                          required
                        />
                      </div>

                      <Input
                        label="Email Address (Optional)"
                        type="email"
                        name="email"
                        placeholder="your.email@example.com"
                        value={formData?.email}
                        onChange={handleChange}
                        error={errors?.email}
                        description="We'll send a confirmation email"
                      />
                    </div>

                    {/* Service Details */}
                    <div className="space-y-4 pt-4 border-t border-border">
                      <h3 className="font-headline font-semibold text-lg text-foreground">
                        Service Details
                      </h3>
                      
                      <Select
                        label="Service Type"
                        options={serviceOptions}
                        value={formData?.serviceType}
                        onChange={(value) => handleSelectChange('serviceType', value)}
                        error={errors?.serviceType}
                        placeholder="Select service"
                        required
                      />

                      <DateTimePicker
                        label="Preferred Date & Time"
                        value={formData?.scheduledDateTime}
                        onChange={(dateTime) => {
                          setFormData(prev => ({ ...prev, scheduledDateTime: dateTime }));
                          if (errors?.scheduledDateTime) {
                            setErrors(prev => ({ ...prev, scheduledDateTime: '' }));
                          }
                        }}
                        error={errors?.scheduledDateTime}
                        required
                        minDate={new Date()}
                      />
                    </div>

                    {/* Additional Information */}
                    <div className="pt-4 border-t border-border">
                      <label className="block font-body font-medium text-sm md:text-base text-foreground mb-2">
                        Additional Details (Optional)
                      </label>
                      <textarea
                        name="message"
                        value={formData?.message}
                        onChange={handleChange}
                        placeholder="Describe your requirements or any specific details..."
                        rows={4}
                        className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all resize-none"
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-4">
                      <Button
                        type="submit"
                        variant="default"
                        size="lg"
                        fullWidth
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Scheduling...' : 'Schedule Service'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="lg"
                        onClick={() => navigate('/contact')}
                      >
                        Contact Us Instead
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <div className="bg-card rounded-lg p-4 border border-border text-center">
                  <Icon name="Clock" size={32} className="mx-auto mb-2 text-primary" />
                  <h4 className="font-headline font-semibold text-sm mb-1">Flexible Scheduling</h4>
                  <p className="font-body text-xs text-muted-foreground">Choose a time that works for you</p>
                </div>
                <div className="bg-card rounded-lg p-4 border border-border text-center">
                  <Icon name="Shield" size={32} className="mx-auto mb-2 text-primary" />
                  <h4 className="font-headline font-semibold text-sm mb-1">Licensed Experts</h4>
                  <p className="font-body text-xs text-muted-foreground">Certified and experienced technicians</p>
                </div>
                <div className="bg-card rounded-lg p-4 border border-border text-center">
                  <Icon name="Phone" size={32} className="mx-auto mb-2 text-primary" />
                  <h4 className="font-headline font-semibold text-sm mb-1">Quick Confirmation</h4>
                  <p className="font-body text-xs text-muted-foreground">We'll call to confirm within 1 hour</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ScheduleService;